// ─── Esquina de avisos (abajo a la derecha) ──────────────────────────────────
// Media docena de avisos flotantes compartían esa esquina, cada uno con su
// propio `right:16px;bottom:16px`: el de versión nueva, el de pendientes, el de
// cierre por inactividad, el de seguridad y el indicador de guardado en la nube.
// Al coincidir dos, uno tapaba al otro y quedaba ilegible.
//
// Aquí viven todos, dentro de un único contenedor que los apila en columna. El
// más urgente queda pegado a la esquina y los demás suben por encima, así que
// aparezcan los que aparezcan, todos se leen enteros.
//
// El contenedor NO intercepta clics (pointer-events:none); cada tarjeta sí, de
// modo que la zona vacía entre avisos no bloquea lo que haya debajo.
const ID_DOCK = "aura-dock";

// Dos cuadros: uno para que el navegador fije el estado inicial (opacidad 0) y
// otro para el final. Con uno solo, ambos caen en el mismo lote de estilo y la
// transición no llega a correr — la tarjeta aparecería de golpe.
function dosCuadros() {
  return new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
}

// Cuanto más alto, más cerca de la esquina. Un aviso que puede cerrar la sesión
// manda sobre uno que solo informa.
export const PRIORIDAD = {
  seguridad: 100,     // la contraseña cambió: la sesión se cierra en 3 minutos
  inactividad: 90,    // cierre por inactividad
  pendientes: 60,     // recordatorio insistente de cosas a medias
  confirmacion: 55,   // "retomaste todos los pendientes"
  version: 30,        // hay una versión nueva del panel
  sync: 10            // guardado en la nube
};

function dock() {
  let el = document.getElementById(ID_DOCK);
  if (!el) {
    el = document.createElement("div");
    el.id = ID_DOCK;
    // column-reverse: el primer hijo del DOM queda ABAJO, pegado a la esquina.
    // z-index por encima de los modales (1000) a propósito: un aviso de cierre
    // de sesión tiene que verse aunque haya un formulario abierto encima.
    el.style.cssText = `
      position:fixed;right:16px;bottom:16px;z-index:1001;
      display:flex;flex-direction:column-reverse;align-items:flex-end;gap:10px;
      max-height:calc(100vh - 32px);pointer-events:none;
    `;
    document.body.appendChild(el);
  }
  return el;
}

// Coloca (o recoloca) una tarjeta en la esquina. La tarjeta no debe traer
// position:fixed ni right/bottom propios: de eso se encarga el contenedor.
export function anclar(el, prioridad) {
  const caja = dock();
  el.dataset.prioridad = String(prioridad);
  el.style.pointerEvents = "auto";
  // Se inserta manteniendo el orden por prioridad descendente: como el
  // contenedor es column-reverse, el primero termina abajo del todo.
  const hermanos = Array.from(caja.children);
  const siguiente = hermanos.find(h => Number(h.dataset.prioridad || 0) < prioridad);
  if (siguiente) caja.insertBefore(el, siguiente);
  else caja.appendChild(el);
  return el;
}

export function desanclar(el) {
  if (el && typeof el._dockLimpiar === "function") { el._dockLimpiar(); el._dockLimpiar = null; }
  if (el && el.parentNode) el.parentNode.removeChild(el);
  const caja = document.getElementById(ID_DOCK);
  if (caja && !caja.children.length) caja.remove();
}

// ─── Tarjetas que se pueden mover con el mouse ───────────────────────────────
// El aviso vive dentro del dock (esquina abajo a la derecha), así que en vez de
// sacarlo del flujo con left/top propios se le aplica un DESPLAZAMIENTO
// (transform: translate). Así el dock sigue apilando y midiendo igual, y la
// tarjeta simplemente se dibuja corrida respecto de su sitio natural.
//
// El desplazamiento se limita para que la tarjeta NUNCA se salga de la ventana
// por ningún lado, y se vuelve a limitar al redimensionar (si la ventana se
// achica, el aviso entra solo en vez de quedar fuera de vista).
const MARGEN_ARRASTRE = 8;   // aire mínimo contra cada borde de la ventana
const posiciones = new Map(); // id de la tarjeta → { dx, dy }

function leerPos(id) {
  if (!id) return { dx: 0, dy: 0 };
  if (posiciones.has(id)) return posiciones.get(id);
  try {
    const p = JSON.parse(localStorage.getItem("erp:dock_pos:" + id) || "null");
    if (p && Number.isFinite(p.dx) && Number.isFinite(p.dy)) { posiciones.set(id, p); return p; }
  } catch { /* sin localStorage: la posición dura lo que dure la sesión */ }
  return { dx: 0, dy: 0 };
}

function guardarPos(id, p) {
  if (!id) return;
  posiciones.set(id, p);
  try { localStorage.setItem("erp:dock_pos:" + id, JSON.stringify(p)); } catch { /* no crítico */ }
}

// Recorta el desplazamiento para que la tarjeta quede dentro de la ventana.
// `base` es dónde caería la tarjeta con desplazamiento cero.
// El Math.max exterior manda: si la tarjeta es más ancha (o más alta) que la
// ventana, se pega al borde izquierdo/superior en vez de escaparse.
function limitar(base, dx, dy) {
  const minDx = MARGEN_ARRASTRE - base.left;
  const maxDx = window.innerWidth - MARGEN_ARRASTRE - base.w - base.left;
  const minDy = MARGEN_ARRASTRE - base.top;
  const maxDy = window.innerHeight - MARGEN_ARRASTRE - base.h - base.top;
  return {
    dx: Math.max(minDx, Math.min(dx, maxDx)),
    dy: Math.max(minDy, Math.min(dy, maxDy))
  };
}

// Posición natural de la tarjeta (sin el desplazamiento que ya tenga aplicado).
function baseDe(el) {
  const r = el.getBoundingClientRect();
  const ap = el._dockPos || { dx: 0, dy: 0 };
  return { left: r.left - ap.dx, top: r.top - ap.dy, w: r.width, h: r.height };
}

// El desplazamiento se publica de DOS formas, y las dos hacen falta:
//
//   · `transform` en línea → sirve para cualquier tarjeta normal.
//   · variables `--dock-dx/--dock-dy` → para tarjetas con animación CSS. Una
//     animación que anime `transform` (como la sacudida del aviso de
//     pendientes) PISA el transform en línea, y la tarjeta se quedaría clavada
//     en su sitio por más que se arrastre. Esas animaciones incluyen las
//     variables en sus keyframes, y así conviven arrastre y animación.
function publicarPos(el, p) {
  el._dockPos = p;
  el.style.setProperty("--dock-dx", p.dx + "px");
  el.style.setProperty("--dock-dy", p.dy + "px");
  el.style.transform = (p.dx || p.dy) ? `translate(${p.dx}px,${p.dy}px)` : "";
}

function aplicarPos(el, pedido) {
  const base = baseDe(el);
  if (!base.w && !base.h) return;             // aún sin dibujar: nada que limitar
  const p = limitar(base, pedido.dx, pedido.dy);
  publicarPos(el, p);
  return p;
}

// Hace que la tarjeta se pueda mover arrastrándola. Los controles (botones,
// enlaces, campos) NO inician arrastre: siguen respondiendo al clic normal.
export function hacerArrastrable(el) {
  if (!el || el._dockArrastrable) return el;
  el._dockArrastrable = true;
  el.style.touchAction = "none";              // en táctil, arrastrar mueve la tarjeta y no la página
  el.style.cursor = "grab";

  aplicarPos(el, leerPos(el.id));

  const alRedimensionar = () => {
    const p = aplicarPos(el, el._dockPos || leerPos(el.id));
    if (p) guardarPos(el.id, p);
  };
  window.addEventListener("resize", alRedimensionar);
  el._dockLimpiar = () => window.removeEventListener("resize", alRedimensionar);

  el.addEventListener("pointerdown", (e) => {
    if (e.button !== 0) return;
    if (e.target.closest("button, a, input, select, textarea, [data-action]")) return;
    const inicio = el._dockPos || { dx: 0, dy: 0 };
    // Mientras se arrastra se congela cualquier animación: si no, la sacudida
    // pelea con el puntero y además falsea la medida de la posición base.
    const animacionPrevia = el.style.animation;
    el.style.animation = "none";
    const base = baseDe(el);
    const x0 = e.clientX, y0 = e.clientY;
    let movido = false;
    try { el.setPointerCapture(e.pointerId); } catch { /* navegador sin captura: el arrastre igual funciona */ }
    el.style.cursor = "grabbing";
    el.style.userSelect = "none";

    const mover = (ev) => {
      publicarPos(el, limitar(base, inicio.dx + (ev.clientX - x0), inicio.dy + (ev.clientY - y0)));
      movido = true;
    };
    const soltar = () => {
      el.removeEventListener("pointermove", mover);
      el.removeEventListener("pointerup", soltar);
      el.removeEventListener("pointercancel", soltar);
      el.style.animation = animacionPrevia;
      el.style.cursor = "grab";
      el.style.userSelect = "";
      if (movido) guardarPos(el.id, el._dockPos);
    };
    el.addEventListener("pointermove", mover);
    el.addEventListener("pointerup", soltar);
    el.addEventListener("pointercancel", soltar);
  });
  return el;
}

// Entrada con fundido: la tarjeta se ancla invisible y se revela en el cuadro
// siguiente, para que el navegador tenga un estado inicial desde el que animar.
export async function anclarConEntrada(el, prioridad) {
  el.style.opacity = "0";
  el.style.transform = "translateY(8px)";
  el.style.transition = "opacity .28s ease, transform .28s ease";
  anclar(el, prioridad);
  await dosCuadros();
  el.style.opacity = "1";
  el.style.transform = "translateY(0)";
  return el;
}
